/**
 * breakevenMockData.ts — Datos demo del punto de equilibrio.
 *
 * QUÉ HACE: genera, de forma DETERMINISTA (sin Math.random), el punto de
 *   equilibrio del mes con una curva acumulada de ventas y su proyección a
 *   cierre. Sirve de respaldo cuando el negocio aún no tiene actividad real.
 *   Con fecha de referencia varía las ventas del mes (factor 0.45–1.15) para que
 *   la navegación ‹ › entre meses muestre coberturas distintas.
 *
 * QUÉ NO DEBE HACER: no lee storage, no hace fetch, no conoce React.
 *
 * Exporta: buildBreakevenMock, BREAKEVEN_MOCK_DATA
 */
import { endOfMonth, format } from "date-fns";
import { es } from "date-fns/locale";
import type { BreakevenData, BreakevenPoint } from "../types/metrics.types";
import { periodVariationFactor } from "./periodVariation";

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

const VENTAS_ACTUALES = 112_000_000;
const PUNTO_EQUILIBRIO = 74_000_000;
const COSTOS_FIJOS = 48_840_000;
const COSTO_VARIABLE = 38_080_000;
const DIAS_TRANSCURRIDOS_BASE = 26;

export function buildBreakevenMock(
  referenceDate: Date = new Date(),
): BreakevenData {
  const now = referenceDate;
  const diasTotales = endOfMonth(now).getDate();
  const today = new Date();
  const isCurrentMonth =
    now.getFullYear() === today.getFullYear() &&
    now.getMonth() === today.getMonth();
  /** Mes cerrado = todos los días reales; mes actual = hasta el día demo. */
  const diasTranscurridos = isCurrentMonth
    ? Math.min(DIAS_TRANSCURRIDOS_BASE, diasTotales)
    : diasTotales;

  const factor = periodVariationFactor(
    "punto-equilibrio",
    "mes",
    now,
    0.45,
    1.15,
  );
  const ventasActuales = Math.round(VENTAS_ACTUALES * factor);
  const proyeccionCierre = Math.round(
    (ventasActuales / diasTranscurridos) * diasTotales,
  );
  const ritmoActual = ventasActuales / diasTranscurridos;
  const ritmoProyectado = proyeccionCierre / diasTotales;

  const points: BreakevenPoint[] = [];
  let cruceDia: number | null = null;
  for (let day = 1; day <= diasTotales; day += 1) {
    const esPasado = day <= diasTranscurridos;
    const acumulado = esPasado ? Math.round(ritmoActual * day) : null;
    const proyeccion =
      day >= diasTranscurridos ? Math.round(ritmoProyectado * day) : null;
    points.push({ label: String(day), acumulado, proyeccion });
    const valor = esPasado ? (acumulado ?? 0) : (proyeccion ?? 0);
    if (cruceDia === null && valor >= PUNTO_EQUILIBRIO) cruceDia = day;
  }

  return {
    label: cap(format(now, "MMMM yyyy", { locale: es })),
    ventasActuales,
    puntoEquilibrio: PUNTO_EQUILIBRIO,
    costosFijos: COSTOS_FIJOS,
    costoVariable: COSTO_VARIABLE,
    margenContribucionPct: 66,
    diasTotales,
    diasTranscurridos,
    cruceDia,
    cruceLabel: cruceDia !== null ? String(cruceDia) : null,
    alcanzado: ventasActuales >= PUNTO_EQUILIBRIO,
    proyeccionCierre,
    points,
  };
}

export const BREAKEVEN_MOCK_DATA: BreakevenData = buildBreakevenMock();
